/**
 * Platform-correct biometric labelling for the "Sign with ..." affordance.
 *
 * A passkey/WebAuthn platform authenticator is called something different on
 * every OS, and showing the wrong name reads as broken to a fiduciary signer:
 *
 *   - Apple iPhone / iPod          → "Face ID"
 *   - Apple Mac (Touch ID)         → "Touch ID"
 *   - Apple iPad (varies by model) → "Face ID or Touch ID"
 *   - Windows                      → "Windows Hello"
 *   - Android                      → "fingerprint"
 *   - anything else / unknown      → "passkey"
 *
 * The chooser is a PURE function of a small navigator-shaped input so it can be
 * unit-tested exhaustively without a browser. `getPlatformBiometricLabel()`
 * wraps it against the real `navigator`. Whether the device actually HAS a
 * platform authenticator is a separate capability probe
 * (`isPlatformAuthenticatorAvailable`) — this only picks the WORD.
 */

export type BiometricPlatform =
  | 'apple-ios'
  | 'apple-ipad'
  | 'apple-mac'
  | 'windows'
  | 'android'
  | 'unknown';

export interface BiometricLabel {
  /** Full button text, e.g. "Sign with Face ID". */
  buttonLabel: string;
  /** Bare method name, e.g. "Face ID", "Windows Hello", "fingerprint", "passkey". */
  methodName: string;
  /** Platform bucket (handy for tests + telemetry). */
  platform: BiometricPlatform;
}

/** The minimal navigator shape the chooser reads — injectable for tests. */
export interface NavigatorLike {
  userAgent?: string;
  platform?: string;
  maxTouchPoints?: number;
  userAgentData?: { platform?: string };
}

/**
 * Pick the correct biometric method name + button label for a device.
 *
 * Detection order is deliberate: modern iPadOS reports a desktop-Mac user agent,
 * so a "macOS" platform WITH multi-touch is treated as an iPad (ambiguous Face
 * ID / Touch ID), while a "macOS" platform withOUT touch is a real Mac
 * (Touch ID).
 */
export function choosePlatformBiometricLabel(nav: NavigatorLike): BiometricLabel {
  const ua = (nav.userAgent ?? '').toLowerCase();
  const uaDataPlatform = (nav.userAgentData?.platform ?? '').toLowerCase();
  const legacyPlatform = (nav.platform ?? '').toLowerCase();
  const platformHint = uaDataPlatform || legacyPlatform;
  const touchPoints = typeof nav.maxTouchPoints === 'number' ? nav.maxTouchPoints : 0;

  const make = (methodName: string, platform: BiometricPlatform): BiometricLabel => ({
    buttonLabel: `Sign with ${methodName}`,
    methodName,
    platform,
  });

  // ── Android (check before Apple: some Android UAs also mention "mobile") ──
  if (ua.includes('android') || platformHint.includes('android')) {
    return make('fingerprint', 'android');
  }

  // ── Apple iPhone / iPod → Face ID ──
  if (ua.includes('iphone') || ua.includes('ipod')) {
    return make('Face ID', 'apple-ios');
  }

  // ── Apple iPad → Face ID or Touch ID (model-dependent, can't distinguish) ──
  //    Explicit iPad UA, OR modern iPadOS masquerading as a touch-capable Mac.
  const isIpad =
    ua.includes('ipad') ||
    ((platformHint.includes('mac') || ua.includes('macintosh')) && touchPoints > 1);
  if (isIpad) {
    return make('Face ID or Touch ID', 'apple-ipad');
  }

  // ── Apple Mac (no touch) → Touch ID ──
  if (platformHint.includes('mac') || ua.includes('macintosh')) {
    return make('Touch ID', 'apple-mac');
  }

  // ── Windows → Windows Hello ──
  if (platformHint.includes('win') || ua.includes('windows')) {
    return make('Windows Hello', 'windows');
  }

  // ── Everything else → the neutral, correct term ──
  return make('passkey', 'unknown');
}

/** Runtime wrapper: choose the label from the real `navigator`. */
export function getPlatformBiometricLabel(): BiometricLabel {
  if (typeof navigator === 'undefined') {
    return {
      buttonLabel: 'Sign with passkey',
      methodName: 'passkey',
      platform: 'unknown',
    };
  }
  return choosePlatformBiometricLabel(navigator as NavigatorLike);
}

/**
 * Is a USER-VERIFYING PLATFORM authenticator (Face ID / Touch ID / Hello /
 * fingerprint) actually available on this device? Async capability probe via
 * the standard WebAuthn API. Returns false safely in jsdom / unsupported
 * browsers (no PublicKeyCredential) — never throws.
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  try {
    if (typeof window === 'undefined') return false;
    const pkc = (window as unknown as { PublicKeyCredential?: unknown }).PublicKeyCredential as
      | { isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean> }
      | undefined;
    if (!pkc || typeof pkc.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') {
      return false;
    }
    return await pkc.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}
